import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../../../../dataManagement/uiDb/UiDb";
import { useNotification } from "../../../../shared/contexts/NotificationContext";
import { useOfferSlider } from "../../ProductDetails/hooks/useOfferSlider";
import { useHasActiveOffer } from "../../ProductDetails/hooks/useHasActiveOffer";
import { useAuthOffer } from "../../ProductDetails/hooks/useAuthOffer";
import { performOffer } from "../../../../../services/helpers/OfferHelper";
import { NotificationType } from "../../../../../constants/Enums";

export interface UseCtaStateResult {
  // offer modal
  isOfferOpen: boolean;
  offerMade: boolean;
  madePrice: number | null;
  isNavigatingToMessages: boolean;
  openOfferModal: () => void;
  handleOfferSuccess: (price: number) => void;
  handleOfferCancel: () => void;
  // slider
  trackRef: React.RefObject<HTMLDivElement>;
  slideX: number;
  sliderStatus: string;
  beginSlide: (e: React.MouseEvent<HTMLSpanElement>) => void;
  beginSlideTouch: (e: React.TouchEvent<HTMLSpanElement>) => void;
  onTrackTap: (e: React.MouseEvent) => void;
  shimmer: boolean;
  KNOB_W: number;
  labelOpacity: () => number;
  resetSlider: () => void;
  // auth
  navigateToLoginForOffer: () => void;
  hasActiveOffer: boolean;
}

export function useCtaState(productId: string, isAuthenticated: boolean): UseCtaStateResult {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useNotification();
  const { navigateToLoginForOffer } = useAuthOffer();
  const hasActiveOffer = useHasActiveOffer(productId);

  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [offerMade, setOfferMade] = useState(false);
  const [madePrice, setMadePrice] = useState<number | null>(null);
  const [isNavigatingToMessages, setIsNavigatingToMessages] = useState(false);

  const handleSliderComplete = useCallback(() => {
    if (hasActiveOffer) {
      triggerNotification(
        "You have an active offer for this product already",
        NotificationType.WARNING
      );
      setTimeout(() => resetSlider(), 500);
      return;
    }
    setIsOfferOpen(true);
  }, [hasActiveOffer]);

  const {
    trackRef, slideX, status: sliderStatus,
    beginSlide, beginSlideTouch, onTrackTap,
    shimmer, KNOB_W, labelOpacity, reset: resetSlider,
  } = useOfferSlider(isAuthenticated ? handleSliderComplete : navigateToLoginForOffer);

  const openOfferModal = useCallback(() => {
    if (!isAuthenticated) { navigateToLoginForOffer(); return; }
    if (hasActiveOffer) {
      triggerNotification("You have an active offer for this product already", NotificationType.WARNING);
      return;
    }
    setIsOfferOpen(true);
  }, [isAuthenticated, hasActiveOffer]);

  const handleOfferSuccess = useCallback((price: number) => {
    setIsOfferOpen(false);
    setOfferMade(true);
    setMadePrice(price);
    if (productId) {
      performOffer(dispatch, price, productId, triggerNotification, () => {
        setIsNavigatingToMessages(true);
        setTimeout(() => {
          navigate("/my-messages");
          setOfferMade(false);
          setMadePrice(null);
          setIsNavigatingToMessages(false);
          resetSlider();
        }, 2000);
      });
    }
  }, [productId, dispatch, navigate, triggerNotification, resetSlider]);

  const handleOfferCancel = useCallback(() => {
    setIsOfferOpen(false);
    resetSlider();
  }, [resetSlider]);

  return {
    isOfferOpen, offerMade, madePrice, isNavigatingToMessages,
    openOfferModal, handleOfferSuccess, handleOfferCancel,
    trackRef, slideX, sliderStatus, beginSlide, beginSlideTouch,
    onTrackTap, shimmer, KNOB_W, labelOpacity, resetSlider,
    navigateToLoginForOffer, hasActiveOffer,
  };
}
